import React, { useEffect, useRef, useState } from "react";
import { AlertTriangle, XCircle, RotateCcw, X, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { usePoll } from "@/hooks/usePoll";

const mins = (s) => (s >= 3600 ? `${Math.round(s / 3600)}h` : `${Math.max(1, Math.round(s / 60))}m`);

export const ImportHealthBanner = () => {
  const { projectId } = useApp();
  const { data: h, refresh } = usePoll(() => (projectId ? api.importHealth(projectId) : Promise.resolve(null)), 10000, [projectId]);
  const [open, setOpen] = useState(false);
  const seen = useRef(new Set());

  useEffect(() => {
    if (!h) return;
    [...h.failed.map((j) => [j.id, `Import failed: ${j.label}`, "error"]),
     ...h.stalled.map((j) => [`s${j.id}`, `Import stalled (${mins(j.idle_seconds)} no progress): ${j.label}`, "warning"]),
     ...h.stalled_uploads.map((u) => [`u${u.id}`, `Upload paused at ${u.pct}%: ${u.filename}`, "warning"])]
      .forEach(([id, msg, lvl]) => { if (!seen.current.has(id)) { seen.current.add(id); toast[lvl](msg); } });
  }, [h]);

  if (!h || h.level === "ok") return null;
  const err = h.level === "error";
  const retry = async () => { const r = await api.importHealthRetry(projectId); toast.success(`Retried ${r.retried} · restarted ${r.restarted}`); refresh(); };
  const dismiss = async () => { await api.importHealthDismiss(projectId); refresh(); };
  const rows = [...h.failed.map((j) => ({ k: j.id, t: j.label, d: j.error })),
    ...h.stalled.map((j) => ({ k: j.id, t: j.label, d: `no progress for ${mins(j.idle_seconds)}` })),
    ...h.stalled_uploads.map((u) => ({ k: u.id, t: u.filename, d: `${u.pct}% uploaded, idle ${mins(u.idle_seconds)} — re-select the file on the Media page to resume` }))];

  return (
    <div data-testid="import-health-banner" className={`border-b px-5 py-2 text-sm ${err ? "bg-[#3b1214] border-[#7f1d1d] text-[#fecaca]" : "bg-[#3a2a0c] border-[#78350f] text-[#fde68a]"}`}>
      <div className="flex items-center gap-3">
        {err ? <XCircle size={16} /> : <AlertTriangle size={16} />}
        <span className="flex-1" data-testid="import-health-summary">
          {h.failed.length > 0 && `${h.failed.length} import(s) failed. `}
          {h.stalled.length > 0 && `${h.stalled.length} download(s) stalled. `}
          {h.stalled_uploads.length > 0 && `${h.stalled_uploads.length} upload(s) paused mid-way. `}
          {h.active_imports > 0 && `${h.active_imports} still running.`}
        </span>
        <button className="flex items-center gap-1 text-xs underline" onClick={() => setOpen(!open)} data-testid="import-health-details"><ChevronDown size={13} />Details</button>
        {(h.failed.length > 0 || h.stalled.length > 0) && <button className="btn btn-ghost !py-0.5 !px-2 text-xs" onClick={retry} data-testid="import-health-retry"><RotateCcw size={12} />Retry</button>}
        <button className="opacity-70 hover:opacity-100" onClick={dismiss} title="Dismiss" data-testid="import-health-dismiss"><X size={15} /></button>
      </div>
      {open && (
        <ul className="mt-2 space-y-1 max-h-40 overflow-y-auto font-mono text-xs" data-testid="import-health-list">
          {rows.slice(0, 50).map((r) => <li key={r.k}><b>{r.t}</b> — {r.d}</li>)}
        </ul>
      )}
    </div>
  );
};
